import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgxPermissionsModule } from 'ngx-permissions';

import { Service } from 'src/app/models/service.model';
import { Permission } from 'src/app/shared/constants/permissions';
import {
  closeDropdown,
  closeDropdownOnOutsideClick,
  stopEvent,
  toggleDropdown,
} from 'src/app/shared/utils/dropdown.util';
import { InlineErrors } from 'src/app/shared/utils/form-validation.util';
import { mapServiceOption } from 'src/app/shared/utils/service-option.util';
import { AutofocusDirective } from 'src/app/shared/directives/autofocus.directive';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-select-single-service',
  templateUrl: './select-single-service.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatIconModule, NgxPermissionsModule, AutofocusDirective],
})
export class SelectSingleServiceComponent implements OnInit {
  @Input() services: Service[];

  @Input() selectedServiceId: string;

  @Input() formRef: UntypedFormGroup;

  @Input() inlineErrors: InlineErrors = {};

  @Output() servicesLoaded = new EventEmitter(false);

  isLoadingServices = signal(true);

  isServiceDropdownOpen = signal(false);

  serviceSearchTerm = signal('');

  serviceOptions = signal<Service[]>([]);

  readonly Permission = Permission;

  readonly iscUrl = environment.iscUrl;

  private elementRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    closeDropdownOnOutsideClick(
      this.elementRef,
      event,
      this.isServiceDropdownOpen,
      this.serviceSearchTerm,
    );
  }

  ngOnInit(): void {
    if (this.services?.length) {
      this.serviceOptions.set(this.services.map(mapServiceOption));

      if (this.selectedServiceId) {
        this.formRef.controls.service?.setValue(this.selectedServiceId);
        this.clearServiceError();
      }

      this.isLoadingServices.set(false);
      this.servicesLoaded.emit(true);
    }
  }

  get filteredServices(): Service[] {
    if (!this.serviceSearchTerm()) return this.serviceOptions();
    const t = this.serviceSearchTerm().toLowerCase();
    return this.serviceOptions().filter(
      (s) =>
        s.Name?.toLowerCase().includes(t) ||
        s.Z_Location__r?.Name?.toLowerCase().includes(t) ||
        s.RecordType?.Name?.toLowerCase().includes(t),
    );
  }

  get selectedService(): Service {
    const serviceId = this.formRef?.value?.service as string;
    return serviceId
      ? this.serviceOptions().find((x) => x.Id === serviceId)
      : null;
  }

  get isServiceRequired(): boolean {
    return this.formRef?.controls.service?.hasValidator(Validators.required);
  }

  toggleServiceDropdown(event: Event): void {
    stopEvent(event);
    toggleDropdown(this.isServiceDropdownOpen, this.serviceSearchTerm);
  }

  selectService(service: Service, event: Event): void {
    stopEvent(event);
    this.formRef.controls.service?.setValue(service.Id);
    this.formRef.controls.locationOfService?.setValue(service.Z_Location__c);
    this.clearServiceError();
    closeDropdown(this.isServiceDropdownOpen, this.serviceSearchTerm);
  }

  onSearchInput(event: Event): void {
    this.serviceSearchTerm.set((event.target as HTMLInputElement).value);
  }

  private clearServiceError(): void {
    this.formRef.controls.service?.updateValueAndValidity();

    if (this.formRef.controls.service?.valid) {
      this.inlineErrors.service = null;
    }
  }
}
